/**
 * This is a template for new plugin wrappers
 *
 * joyo-native-service
 * Add by JoyoDuan on 2017-12-05
 *
 * TODO:
 * - Add/Change information below
 * - Document usage (importing, executing main functionality)
 * - Remove any imports that you are not using
 * - Add this file to /src/index.ts (follow style of other plugins)
 * - Remove all the comments included in this template, EXCEPT the @Plugin wrapper docs and any other docs you added
 * - Remove this note
 *
 */
import { Injectable } from '@angular/core';
import { Observable, Observer, Subscription } from 'rxjs';

import { Platform, Keyboard } from 'ionic-angular';

// 原生插件
import { StatusBar } from '@ionic-native/status-bar';
import { SplashScreen } from '@ionic-native/splash-screen';
import { AppVersion } from '@ionic-native/app-version'; // npm install --save @ionic-native/app-version
import { Network } from '@ionic-native/network';  // npm install --save @ionic-native/network
import { AppMinimize } from '@ionic-native/app-minimize';
import { InAppBrowser } from '@ionic-native/in-app-browser';
// 1.ionic cordova plugin add cordova.plugins.diagnostic  2.npm install --save @ionic-native/diagnostic
import { Diagnostic } from '@ionic-native/diagnostic';
// 二维码扫描插件 1.ionic cordova plugin add phonegap-plugin-barcodescanner  2.npm install --save @ionic-native/barcode-scanner
import { BarcodeScanner } from '@ionic-native/barcode-scanner';
// 拨打电话插件 1.ionic cordova plugin add call-number  2.npm install --save @ionic-native/call-number
import { CallNumber } from '@ionic-native/call-number';    // 1.ionic cordova plugin add call-number  2.npm install --save @ionic-native/call-number
import { SMS, SmsOptions } from '@ionic-native/sms';    // 1.ionic cordova plugin add cordova-sms-plugin 2.npm install --save @ionic-native/sms
import { CodePush } from '@ionic-native/code-push';
import { BackgroundMode } from '@ionic-native/background-mode';   // 1.ionic cordova plugin add cordova-plugin-background-mode  2.npm install --save @ionic-native/background-mode
// 时间选择 // 1.ionic cordova plugin add cordova-plugin-datepicker  2.npm install --save @ionic-native/date-picker
import { DatePicker, DatePickerOptions } from '@ionic-native/date-picker';
// 本地通知
import { LocalNotifications, ILocalNotification } from '@ionic-native/local-notifications'; // 1.ionic cordova plugin add cordova-plugin-local-notification  2.npm install --save @ionic-native/local-notifications
// 保持屏幕常亮
import { Insomnia } from '@ionic-native/insomnia'; // 1.ionic cordova plugin add cordova-plugin-insomnia  2.npm install @ionic-native/insomnia

// 服务
import { Utils } from 'joyo-utils';
import { GlobalData } from 'joyo-global-data';
import { DataService } from 'joyo-data-service';
import { Dialogs } from 'joyo-dialogs';
import { Logger } from 'joyo-logger';

declare var window: any;
declare var UID: any;
declare var ChannelPackagePlugin: any;


/**
 * 原生app服务
 * @export
 * @class NativeService
 */

export class NativeService {
  private disconnectSubscription: Subscription;    // 网络连接中断订阅
  private connectSubscription: Subscription;   // 网络连接已连接订阅

  constructor(private platform: Platform, private statusBar: StatusBar, private splashScreen: SplashScreen,
    private codePush: CodePush, private keyboard: Keyboard, private backgroundMode: BackgroundMode, private datePicker: DatePicker,
    private appVersion: AppVersion, private network: Network, private appMinimize: AppMinimize, private inAppBrowser: InAppBrowser,
    private diagnostic: Diagnostic, private barcodeScanner: BarcodeScanner, private cn: CallNumber, private sms: SMS,
    private logger: Logger, private globalData: GlobalData, private dataService: DataService,
    private localNotifications: LocalNotifications,
    private insomnia: Insomnia, private dialogs: Dialogs
  ) {
  }


  /******************************* 原生 ************************************* */
  /**
   * 热更新同步代码
   * @returns
   * @memberof NativeService
   */
  sync() {
    // 如果不是真机环境return
    if (!this.isMobile()) return;

    // 从配置文件获取配置
    this.dataService.getBasicConfig().subscribe((res: any) => {

      // 发布的key
      let deploymentKey = '';

      // 如果是Android环境 并且是 debug模式
      if (this.isAndroid()) {
        if (res.debug.enable) {
          deploymentKey = res.codePush.deploymentKey.android.staging;
        } else {

        }
      }
      if (this.isAndroid() && !res.debug.enable) {
        deploymentKey = res.codePush.deploymentKey.android.production;
      }
      if (this.isIOS() && res.debug.enable) {
        deploymentKey = res.codePush.deploymentKey.ios.staging;
      }
      if (this.isIOS() && !res.debug.enable) {
        deploymentKey = res.codePush.deploymentKey.ios.production;
      }

      // 热更新同步
      this.codePush.sync({
        deploymentKey: deploymentKey
      }).subscribe((syncStatus: any) => {
        // this.logger.info('热更新返回信息', {deploymentKey: deploymentKey, syncStatus: syncStatus});
      });
    });
  }


  /**
   * 使用默认状态栏
   */
  statusBarStyleDefault(): void {
    this.statusBar.styleDefault();
  }


  /**
   * 设置十六进制的状态栏颜色
   *
   * @param hexColor 如"#50000000"，其中前两位"50"为透明度，后六位"000000"为rgb颜色
   */
  setStatusBarBackgroundColorByHexString(hexColor: string): void {
    this.statusBar.backgroundColorByHexString(hexColor);
  }


  /**
   * 显示状态栏
   */
  showStatusBar(): void {
    this.statusBar.show();
  }


  /**
   * 隐藏状态栏
   */
  hideStatusBar(): void {
    this.statusBar.hide();
  }


  /**
   * 隐藏启动页面
   */
  hideSplashScreen(): void {
    this.splashScreen.hide();
  }


  /**
   * 是否是cordova环境
   * 
   * @description
   * @author JoyoDuan
   * @date 2020-09-15
   * @returns {boolean}
   */
  isCordova(): boolean {
    return this.platform.is('cordova');
  }


  /**
   * 是否是真机环境
   * @returns {boolean}
   * @memberof NativeService
   */
  isMobile(): boolean {
    return this.platform.is('mobile') && !this.platform.is('mobileweb');
  }


  /**
   * 是否android真机环境
   * @returns {boolean}
   * @memberof NativeService
   */
  isAndroid(): boolean {
    return this.isMobile() && this.platform.is('android');
  }


  /**
   * 是否是ios真机环境
   * @returns {boolean}
   * @memberof NativeService
   */
  isIOS(): boolean {
    return this.isMobile && (this.platform.is('ios') || this.platform.is('ipad') || this.platform.is('iphone'));
  }


  /**
   * 判断是否有网络连接
   * @returns {boolean}
   * @memberof NativeService
   */
  isConnecting(): boolean {
    return this.getNetworkType() !== 'none';
  }


  /**
   * 获取网络类型 如:`unknown`, `ethernet`, `wifi`, `2g`, `3g`, `4g`, `cellular`:蜂窝, `none`
   * @returns {string}
   * @memberof NativeService
   */
  getNetworkType(): string {
    // this.logger.info('网络连接类型', {type: this.network.type});
    if (!this.isMobile())
      return 'wifi';
    return this.network.type;
  }


  /**
   * 监测网络连接断开
   * @param callBack
   */
  monitorNetworkDisconnect(callBack?: (...args: any[]) => void): void {
    this.disconnectSubscription = this.network.onDisconnect().subscribe(() => {
      // this.logger.info('monitorNetworkDisconnect - 网络连接中断');
      if (callBack) callBack();
    });
  }


  /**
   * 停止监测网络断开连接
   * @param callBack
   */
  stopMonitorNetworkDisconnect(callBack?: (...args: any[]) => void): void {
    this.disconnectSubscription.unsubscribe();
    if (callBack) callBack();
  }


  /**
   * 监测网络已连接
   * @param {(...args: any[]) => void} [callBack]
   * @memberof NativeService
   */
  monitorNetworkConnect(callBack?: (...args: any[]) => void): void {
    this.connectSubscription = this.network.onConnect().subscribe(() => {
      // this.logger.info('monitorNetworkConnect - 网络已连接');
      if (callBack) callBack();
    });
  }


  /**
   * 停止监测网络已连接
   * @param {(...args: any[]) => void} [callBack]
   * @memberof NativeService
   */
  stopMonitorNetworkConnect(callBack?: (...args: any[]) => void): void {
    this.connectSubscription.unsubscribe();
    if (callBack) callBack();
  }


  /**
   * 获取APP版本号
   * @returns
   * @memberof NativeService
   *
   */
  getAppVersion(): Observable<string> {
    return new Observable((observer: Observer<any>) => {
      this.appVersion.getVersionNumber().then((value: string) => {
        observer.next(value);
      }).catch((error) => {
        this.logger.error( '获取APP版本号失败', error, {error: error});
        observer.error(error);
      });
    });
  }


  /**
   * 获取App Name 如现场作业, 对应/config.xml中name的值
   * @returns {Observable<string>}
   * @memberof NativeService
   */
  getAppName(): Observable<string> {
    return new Observable((observable: Observer<any>) => {
      this.appVersion.getAppName().then((value: string) => {
        observable.next(value);
      }).catch((error) => {
        this.logger.error('获取App Name失败', error);
        observable.error(error);
      });
    });
  }


  /**
   * 获取App的包名,  /id,如com.joyo.app, 对应/config.xml中id的值
   * @returns {Observable<string>}
   * @memberof NativeService
   */
  getPackageName(): Observable<string> {
    return new Observable((observable: Observer<any>) => {
      this.appVersion.getPackageName().then((value: string) => {
        observable.next(value);
      }).catch((error) => {
        this.logger.error('获取App包名失败', error);
        observable.error(error);
      });
    });
  }


  /**
   * 调用原生最小化App插件
   * 仅用于Android
   * @memberof NativeService
   */
  minimize(): void {
    this.appMinimize.minimize();
  }


  /**
   * 开启后台模式运行
   * @memberof NativeService
   */
  enableBackgroundMode(): void {
    this.backgroundMode.enable();
  }


  /**
   * 停用后台模式运行
   * @memberof NativeService
   */
  disableBackgroundMode(): void {
    this.backgroundMode.disable();
  }


  /**
   * 进入前台运行模式
   * @memberof NativeService
   */
  foreground(): void {
    this.backgroundMode.moveToForeground();
  }


  /**
   * 进入后台运行模式
   * @memberof NativeService
   */
  background(): void {
    this.backgroundMode.moveToBackground();
  }


  /**
   * 通过系统浏览器打开URL
   * @param {string} url
   * @param {string} target
   * @memberof NativeService
   */
  openURLByBrowser(url: string, target: string = '_system'): void {
    this.inAppBrowser.create(url, target); // _self, _blank, _system
  }


  /**
   * 扫描二维码
   * @returns {Observable<any>}
   * @memberof NativeService
   */
  scan(): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.barcodeScanner.scan().then((barcodeData) => {
        observer.next(barcodeData.text);
      }).catch((error) => {
        this.logger.error('扫描二维码失败', error);
        observer.error(error);
      });
    });
  }


  /**
   * 拨打电话
   * @param {string} callNumber
   * @param {boolean} [bypassAppChooser=true] 绕过程序选择器，直接拨号
   * @memberof NativeService
   */
  callNumber(callNumber: string, bypassAppChooser: boolean = true): void {
    this.cn.callNumber(callNumber, bypassAppChooser).then((res) => {
      // this.logger.info('Launched dialer!拨打电话', {callNumber: callNumber, result: res});
    }).catch(error => {
      this.logger.error('拨打电话失败', error, {callNumber: callNumber});
    });
  }


  /**
   * 发送短信
   * @param phoneNumber
   * @param message
   * @param options SmsOptions: {replaceLineBreaks: boolean (替换换行), android: SmsOptionsAndroid = {intent: string}}
   */
  sendSMS(phoneNumber: string | Array<string>, message?: string, options: SmsOptions = {}): void {
    // 扩展options
    options = Object.assign(options, {
      replaceLineBreaks: false,   // 替换换行
      android: {    // android: SmsOptionsAndroid --> {intent: string}
        intent: 'INTENT'
      }
    });
    this.sms.send(phoneNumber, message, options).then((res) => {
      // this.logger.info('短信发送返回：', {phoneNumber: phoneNumber, result: res});
    }).catch((error) => {
      this.logger.error('短信发送失败', error, {phoneNumber: phoneNumber});
    });
  }


  /**
   * 初始化定位权限
   *
   * @returns {Observable<boolean>}
   * NativeService
   */
  public initLocationAuthorization(isToSettings?: boolean): Observable<boolean> {
    return new Observable((observer: Observer<boolean>) => {
      if (this.isMobile()) {
        // 检测是否打开定位服务
        this.assertLocationService().subscribe((serviceRes: boolean) => {
          if (null == serviceRes) {
            this.logger.log('initLocationAuthorization -> assertLocationService: ', serviceRes);

            if (isToSettings) {
              this.dialogs.confirm('您未开启位置服务', '获取位置信息', () => {
                this.diagnostic.switchToLocationSettings();

                this.initLocationAuthorization(false).subscribe((locationAuthorization: boolean) => {
                  observer.next(locationAuthorization);
                }, (error: any) => {
                  observer.error(error);
                });
              }, '去开启', () => { // 点击取消，退出app/返回false
                // this.platform.exitApp();
                observer.next(null);
              });
              return;
            }

            observer.next(serviceRes);
            return;
          }

          // 没有开启定位服务
          if (false == serviceRes) {
            observer.next(serviceRes);
            return;
          }

          // 检测是否有定位权限
          this.assertLocationAuthorization().subscribe((locationRes: boolean) => {
            // 工信部不允许重复获取弹窗获取权限
            // 进入设置开启后，不确定开启没有，再次检测
            if (null == locationRes) {
              this.logger.log('initLocationAuthorization -> assertLocationAuthorization: ', locationRes);

              if (isToSettings) {
                this.dialogs.confirm('请在手机设置或权限管理中开启', '缺少定位权限', () => {
                  // 进入设置，不知道用户开启没有，所以返回null，当为null时再次检测
                  this.diagnostic.switchToSettings();

                  this.initLocationAuthorization(false).subscribe((authorizationRes) => {
                    observer.next(authorizationRes);
                  }, (error) => {
                    observer.error(error);
                  });
                }, '去开启', () => { // 取消，退出app/返回false
                  // this.platform.exitApp();
                  observer.next(false);
                });

                return;
              }
            }

            observer.next(locationRes);
          }, (error: any) => {
            observer.error(error);
          });
        }, (error: any) => {
          observer.error(error);
        });
      } else { // 非手机环境，无需权限
        observer.next(true);
      }
    });
  }


  /**
   * 检测app位置服务是否开启
   * 闭包函数
   */
  private assertLocationService = (() => {  // 自执行函数,使用闭包保存enabledLocationService变量
    let enabledLocationService = false;   // 手机是否开启定位服务
    return () => {
      return new Observable((observer: Observer<any>) => {
        if (enabledLocationService) {
          observer.next(true);
        } else {
          this.diagnostic.isLocationEnabled().then((enabled) => {
            if (enabled) {
              enabledLocationService = true;
              observer.next(true);
            } else {
              enabledLocationService = false;
              observer.next(null);
            }
          }).catch((error) => {
            this.logger.error('调用diagnostic.isLocationEnabled方法失败', error);
            observer.error(error);
          });
        }
      });
    };
  })();


  /**
   * 检测app是否有定位权限
   *
   * 闭包函数
   */
  private assertLocationAuthorization = (() => {
    let locationAuthorization = false;
    return () => {
      return new Observable((observer: Observer<any>) => {
        if (locationAuthorization) {
          observer.next(true);
        } else {
          this.diagnostic.isLocationAuthorized().then(res => {
            if (res) {
              locationAuthorization = true;
              observer.next(true);
            } else {
              this.diagnostic.requestLocationAuthorization('always').then(res => { // 请求定位权限
                this.globalData.isDebug && console.log('请求定位权限:', res);

                // 拒绝访问状态，必须手动开启
                if (res === 'DENIED_ALWAYS') {
                  locationAuthorization = false;
                  // 返回null，表示拒绝且不再提示
                  observer.next(null);
                } else if (res === 'DENIED_ONCE' || res === 'NOT_REQUESTED' || res === 'DENIED') { // 拒绝
                  locationAuthorization = false;
                  observer.next(false);
                } else { // GRANTED: 授予 GRANTED_WHEN_IN_USE: 使用时
                  locationAuthorization = true;
                  observer.next(true);
                }
              }).catch((error) => {
                this.logger.error('调用diagnostic.requestLocationAuthorization方法失败', error);
                // observer.error(error);
                // API 22/23调用异常，直接返回true
                observer.next(true);
              });
            }
          }).catch((error) => {
            this.logger.error(error, '调用diagnostic.isLocationAvailable方法失败', error);
            // observer.error(error);
            // API 22/23调用异常，直接返回true
            observer.next(true);
          });
        }
      });
    };
  })();


  /**
   * 检测app是否有读取存储权限
   * 闭包函数
   */
  public externalStoragePermissionsAuthorization = (() => {
    let havePermission = false;
    return () => {
      return new Observable((observer: Observer<any>) => {
        if (havePermission) {
          observer.next(true);
        } else {
          let permissions = [this.diagnostic.permission.READ_EXTERNAL_STORAGE, this.diagnostic.permission.WRITE_EXTERNAL_STORAGE];
          this.diagnostic.getPermissionsAuthorizationStatus(permissions).then(res => {
            // 有权限
            if (res.READ_EXTERNAL_STORAGE === 'GRANTED' && res.WRITE_EXTERNAL_STORAGE === 'GRANTED') {
              havePermission = true;
              observer.next(true);
            } else {
              havePermission = false;
              // 请求权限
              this.diagnostic.requestRuntimePermissions(permissions).then(res => {
                if (res.READ_EXTERNAL_STORAGE === 'GRANTED' && res.WRITE_EXTERNAL_STORAGE === 'GRANTED') {
                  havePermission = true;
                  observer.next(true);
                } else {
                  havePermission = false;
                  observer.next(null);
                }
              }).catch((error) => {
                this.logger.error('调用diagnostic.requestRuntimePermissions方法失败', error);
                // observer.error(error);
                // API 22/23调用异常，直接返回true
                observer.next(true);
              });
            }
          }).catch((error) => {
            this.logger.error('调用diagnostic.getPermissionsAuthorizationStatus方法失败', error);
            // observer.error(error);
            // API 22/23调用异常，直接返回true
            observer.next(true);
          });
        }
      });
    };
  })();


  /**
   * 自定义请求权限
   *
   * @param permission 需要请求的权限
   * @param permissionName 如：缺少读取存储权限
   * @param cancelHandler 取消开启权限的处理事件
   */
  public requestPermissionAuthorization(permission: any, permissionName: string, cancelHandler?: () => void): void {
    this.diagnostic.getPermissionAuthorizationStatus(permission).then((res: any) => {
      // 已开启
      if (res === 'GRANTED') {
        this.logger.log(`getPermissionAuthorizationStatus - ${permission}:`, res);
      } else { // 未开启
        this.diagnostic.requestRuntimePermission(permission).then((res: any) => {
          // 有权限
          if (res[permission] === 'GRANTED') {
            this.logger.log(`requestRuntimePermission - ${permission}:`, res);
          } else {
            this.dialogs.confirm('请在手机设置或权限管理中开启', permissionName, () => {
              this.diagnostic.switchToSettings();
            }, '去开启', cancelHandler);
          }
        });
      }
    });
  }


  /**
   * 检测app是否有读取手机信息
   *
   * 闭包函数
   */
  public phoneStatePermissionsAuthorization = (() => {
    let phoneStateAuthorization = false;
    return () => {
      return new Observable((observer: Observer<any>) => {
        if (phoneStateAuthorization) {
          observer.next(true);
        } else {
          // 判断是否有权限
          this.diagnostic.getPermissionAuthorizationStatus(this.diagnostic.permission.READ_PHONE_STATE).then((res: any) => {
            if (res === 'GRANTED' || res === true) {
              phoneStateAuthorization = true;
              observer.next(true);
            } else {
              // 请求权限
              this.diagnostic.requestRuntimePermission(this.diagnostic.permission.READ_PHONE_STATE).then((res: any) => {
                this.logger.log('请求设备信息权限', res);

                // 拒绝访问状态，必须手动开启
                if (res === 'DENIED_ALWAYS') {
                  phoneStateAuthorization = false;
                  observer.next(null);
                } else if (res === 'DENIED_ONCE' || res === 'NOT_REQUESTED' || res === 'DENIED') { // 拒绝
                  phoneStateAuthorization = false;
                  observer.next(false);
                } else { // GRANTED: 授予 GRANTED_WHEN_IN_USE: 使用时
                  phoneStateAuthorization = true;
                  observer.next(true);
                }
              }).catch((error) => {
                this.logger.error('调用diagnostic.requestRuntimePermission(this.diagnostic.permission.READ_PHONE_STATE)方法失败', error);
                // observer.error(error);
                // API 22/23调用异常，直接返回true
                observer.next(true);
              });
            }
          }).catch((error: any) => {
            this.logger.error(error, '调用diagnostic.getPermissionAuthorizationStatus(this.diagnostic.permission.READ_PHONE_STATE)方法失败', error);
            // observer.error(error);
            // API 22/23调用异常，直接返回true
            observer.next(true);
          });
        }
      });
    };
  })();


  /**
   * 初始化设备信息权限
   *
   * @description
   * @author JoyoDuan
   * @date 2020-09-16
   * @param {boolean} [isToSettings]
   * @returns
   */
  public initPhoneStateAuthorization(isToSettings?: boolean): Observable<boolean> {
    return new Observable((observer: Observer<boolean>) => {
      if (this.isMobile()) {
        // 检测是否有定位权限
        this.phoneStatePermissionsAuthorization().subscribe((res: boolean) => {
          this.logger.info('initPhoneStateAuthorization - 检测设备信息权限:', {
            res: res
          });
          // 进入设置开启后，不确定开启没有，再次检测
          if (res === null) {
            this.logger.log('initPhoneStateAuthorization - 再次获取设备信息权限:', res);

            if (isToSettings) {
              this.dialogs.confirm('请在手机设置或权限管理中开启', '缺少设备信息权限', () => {
                // 进入设置，不知道用户开启没有，所以返回null，当为null时再次检测
                this.diagnostic.switchToSettings();

                this.initPhoneStateAuthorization(false).subscribe((authorizationRes) => {
                  observer.next(authorizationRes);
                }, (error) => {
                  observer.error(error);
                });
              }, '去开启', () => { // 取消，退出app/返回false
                observer.next(false);
              });

              return;
            }
          }

          observer.next(res);
        });
      } else { // 非手机环境，无需权限
        observer.next(true);
      }
    });
  }


  /**
   * 获取日期时间
   * https://ionicframework.com/docs/native/date-picker/
   *
   * @param {DatePickerOptions} [opts]
   * @returns {Observable<any>}
   * @memberof NativeService
   */
  getDateTime(opts?: DatePickerOptions): Observable<any> {

    return new Observable((observer: Observer<any>) => {
      let options: DatePickerOptions = {
        mode: 'datetime',   // date，日期 | time，时间 | datetime，日期时间
        date: new Date(),
        titleText: '选择时间',
        okText: '确定',
        cancelText: '取消',
        todayText: '今天',
        nowText: '现在',
        is24Hour: true,
        // 1: THEME_TRADITIONAL | 2: THEME_HOLO_DARK | 3: THEME_HOLO_LIGHT | 4: THEME_DEVICE_DEFAULT_DARK | 5: THEME_DEVICE_DEFAULT_LIGHT
        androidTheme: this.datePicker.ANDROID_THEMES.THEME_DEVICE_DEFAULT_LIGHT
      };

      // 如果opts存在，则扩展options
      if (Utils.isNotEmptyObject(opts)) options = Object.assign(options, opts);

      // 显示选择时间窗口
      this.datePicker.show(options).then((date: Date) => {
        observer.next(date);
      }).catch((error: any) => {
        observer.error(error);
      });
    });

  }


  /**
   * 关闭原生键盘
   *
   * @memberof NativeService
   */
  public closeKeyboard(): void {
    if (this.keyboard.isOpen()) {
      this.keyboard.close();
    }
  }


  /**
   * 屏幕保持常亮
   *
   * @memberof NativeService
   */
  public insomniaKeepAwake(): void {
    this.insomnia.keepAwake().then((res: any) => {
      this.logger.log('调用屏幕常亮(insomnia.keepAwake)返回:', {res: res});
    }).catch((error: any) => {
      this.logger.log('调用屏幕常亮(insomnia.keepAwake)出错:', {error: error});
      this.logger.error('调用屏幕常亮(insomnia.keepAwake)出错:', {error: error});
    });
  }


  /**
   * 屏幕常亮一次
   *
   * @memberof NativeService
   */
  public insomniaAllowSleepAgain(): void {
    this.insomnia.allowSleepAgain().then((res: any) => {
      this.logger.log('调用屏幕常亮一次(insomnia.allowSleepAgain)返回:', {res: res});
    }).catch((error: any) => {
      this.logger.log('调用屏幕常亮一次(insomnia.allowSleepAgain)出错:', {error: error});
      this.logger.error('调用屏幕常亮一次(insomnia.allowSleepAgain)出错:', {error: error});
    });
  }


  /**
   * 将本地路径转为合法的图片路径
   * 
   * @param url 
   */
  public convertFileSrc(url: string): string {
    this.logger.log('WebView convertFileSrc前的url:', url);
    try {
      return window.Ionic.WebView.convertFileSrc(url);
    } catch (e) {
      this.logger.error('window.Ionic.WebView.convertFileSrc Error', e, {
        url: url
      });
      return url;
    }
  }


  /*************************************************** 本地消息通知 start ******************************************************* */
  /**
   * 调用本地消息通知
   * https://ionicframework.com/docs/native/local-notifications/
   *
   * @param {number} id
   * @param {string} text
   * @param {string} [title]
   * @param {ILocalNotification} [options]
   * @memberof NativeService
   */
  public localNotifySchedule(id: number, text: string, title?: string, opts?: ILocalNotification): void {
    let options: ILocalNotification = {
      id: id, // 消息通知的Id
      title: title || '', // 标题
      text: text, // 显示内容
      sound: '', // 通知的声音
      // silent: true, // 是否使用消息通知
      launch: true, // 点击消息通知是否打开app
      wakeup: true, // 收到通知时，唤醒app
      timeoutAfter: 5 * 60 * 1000, // 5分钟超时
      clock: false, // 时钟
      progressBar: { // 进度圈配置
        enabled: false, // 是否使用进度圈
        value: 0, // 进度圈的值
        maxValue: 100, // 进度圈最大值
        indeterminate: false,
        // description: '描述',
        // status: '状态'
      },
      sticky: true, // 粘性的，是否可以左右滑动删除消息通知
      autoClear: false, // 点击通知清除通知
      lockscreen: true // 锁屏是否显示通知
    };

    // 扩展设置
    if (Utils.isNotEmptyObject(opts)) {
      options = Object.assign(options, opts);
    }
    // 执行本地消息通知
    this.localNotifications.schedule(options);
  }


  /**
   * 修改本地消息通知
   * https://ionicframework.com/docs/native/local-notifications/
   *
   * @param {number} id
   * @param {string} text
   * @param {string} [title]
   * @param {ILocalNotification} [options]
   * @memberof NativeService
   */
  public localNotifyUpdate(id: number, text: string, title?: string, opts?: ILocalNotification): void {
    let options: ILocalNotification = {
      id: id, // 消息通知的Id
      title: title || '', // 标题
      text: text, // 显示内容
      sound: '', // 通知的声音
      // silent: true, // 是否使用消息通知
      launch: true, // 点击消息通知是否打开app
      wakeup: true, // 收到通知时，唤醒app
      timeoutAfter: 5 * 60 * 1000, // 5分钟超时
      clock: false, // 时钟
      progressBar: { // 进度圈配置
        enabled: false, // 是否使用进度圈
        value: 0, // 进度圈的值
        maxValue: 100, // 进度圈最大值
        indeterminate: false,
        // description: '描述',
        // status: '状态'
      },
      sticky: true, // 粘性的，是否可以左右滑动删除消息通知
      autoClear: false, // 点击通知清除通知
      lockscreen: true // 锁屏是否显示通知
    };

    // 扩展设置
    if (Utils.isNotEmptyObject(opts)) {
      options = Object.assign(options, opts);
    }
    // 修改本地消息通知
    this.localNotifications.update(options);
  }


  /**
   * 清除本地消息通知
   *
   * @param id
   */
  public localNotifyClear(id: number): void {
    this.localNotifications.clear(id);
  }


  /**
   * 清除所有本地消息通知
   *
   * @memberof NativeService
   */
  public localNotifyClearAll(): void {
    this.localNotifications.clearAll();
  }


  /**
   * 判断是否有本地通知的权限
   *
   * @returns {Observable<boolean>}
   * @memberof NativeService
   */
  public localNotifyHasPermission(): Observable<boolean> {
    return new Observable((observer: Observer<boolean>) => {
      this.localNotifications.hasPermission().then((res: any) => {
        this.logger.log('判断是否有本地消息通知权限返回：', {hasPermission: res});
        observer.next(res);
      }).catch((error: any) => {
        this.logger.error('判断是否有本地消息通知权限出错', error, {error: error});
        observer.error(error);
      });
    });
  }


  /**
   * 判断是否有本地通知的权限
   *
   * @returns {Observable<boolean>}
   * @memberof NativeService
   */
  public localNotifyRequestPermission(): Observable<boolean> {
    return new Observable((observer: Observer<boolean>) => {
      this.localNotifications.requestPermission().then((res) => {
        this.logger.log('请求本地通知权限返回：', {requestPermission: res});
        observer.next(res);
      }).catch((error: any) => {
        this.logger.error('请求本地通知权限出错', error, {error: error});
        observer.error(error);
      });
    });
  }
  /*************************************************** 本地消息通知 end ******************************************************* */


  /**
   * 获取设备唯一识别码
   * 
   * @description
   * @author JoyoDuan
   * @date 2020-09-16
   */
  public getUID(): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      // IOS设备
      if (this.isIOS()) {
        UID.getDeviceID((uid: any) => {
          if (Utils.isNotEmptyStr(uid)) {
            uid = uid.toString().replace('-', '');
          }
          observer.next({
            'UID': uid
          });
        }, (error: any) => {
          this.logger.error('UID.getDeviceID出错', error);
          observer.error(error);
        });
      } else {
        // 初始化权限
        this.initPhoneStateAuthorization(false).subscribe((res: any) => {
          // 有权限
          if (res) {
            UID.getUID((uid: any) => {
              observer.next(uid);
            }, (error: any) => {
              this.logger.error('调用“UID.getUID”获取设备唯一标识出错：', error);
              observer.error(error);
            });
          } else {
            observer.next('Permission Denied');
          }
        }, (error: any) => {
          observer.error(error);
        });
      }
    });
  }


  /**
   * 获取APP渠道来源
   *
   * @Author JoyoDuan
   * @Date 2020/11/13
   */
  public getChannel(): Observable<string> {
    return new Observable((observer: Observer<string>) => {
      // Android环境
      if (this.isAndroid()) {
        ChannelPackagePlugin.getChannel((channel: any) => {
          observer.next(channel);
        }, (error: any) => {
          observer.error(error);
        }, "JOYO_CHANNEL", "own");
      } else {
        observer.next('ios');
      }
    });
  }
  /*************************************************** 原生 End ******************************************************* */
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Platform, },
{type: StatusBar, },
{type: SplashScreen, },
{type: CodePush, },
{type: Keyboard, },
{type: BackgroundMode, },
{type: DatePicker, },
{type: AppVersion, },
{type: Network, },
{type: AppMinimize, },
{type: InAppBrowser, },
{type: Diagnostic, },
{type: BarcodeScanner, },
{type: CallNumber, },
{type: SMS, },
{type: Logger, },
{type: GlobalData, },
{type: DataService, },
{type: LocalNotifications, },
{type: Insomnia, },
{type: Dialogs, },
];
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
